import { useEffect, useMemo, useRef } from "react";
import { animate } from "animejs";
import usePrefersReducedMotion from "../../hooks/usePrefersReducedMotion";

const parseNumber = (value) => {
  const raw = String(value ?? "");
  const match = raw.match(/^\s*(\d+(?:\.\d+)?)(.*)$/);

  if (!match) {
    return { target: 0, suffix: raw, decimals: 0, valid: false };
  }

  const decimals = match[1].includes(".") ? match[1].split(".")[1].length : 0;

  return {
    target: Number(match[1]),
    suffix: match[2] ?? "",
    decimals,
    valid: true
  };
};

const AnimatedNumber = ({ value, className = "", duration = 850, delay = 0 }) => {
  const numberRef = useRef(null);
  const hasAnimated = useRef(false);
  const reduceMotion = usePrefersReducedMotion();
  const parsed = useMemo(() => parseNumber(value), [value]);

  useEffect(() => {
    const node = numberRef.current;
    if (!node) return undefined;

    if (!parsed.valid) {
      node.textContent = parsed.suffix;
      return undefined;
    }

    const finish = () => {
      node.textContent = parsed.target.toFixed(parsed.decimals);
    };

    if (reduceMotion) {
      finish();
      return undefined;
    }

    const run = () => {
      if (hasAnimated.current) return;
      hasAnimated.current = true;

      const counter = { current: 0 };
      node.textContent = parsed.decimals ? Number(0).toFixed(parsed.decimals) : "0";

      const animation = animate(counter, {
        current: parsed.target,
        duration,
        delay,
        ease: "outExpo",
        onUpdate: () => {
          node.textContent = parsed.decimals
            ? counter.current.toFixed(parsed.decimals)
            : String(Math.round(counter.current));
        },
        onComplete: finish
      });

      node._counterAnimation = animation;
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        run();
        observer.disconnect();
      },
      { threshold: 0.35 }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      node._counterAnimation?.cancel?.();
    };
  }, [delay, duration, parsed.decimals, parsed.suffix, parsed.target, parsed.valid, reduceMotion]);

  return (
    <span className={className} aria-label={`${parsed.target}${parsed.suffix}`}>
      <span ref={numberRef}>{reduceMotion ? parsed.target : 0}</span>
      {parsed.suffix}
    </span>
  );
};

export default AnimatedNumber;
